import React from 'react';

type Props = {
    children: React.ReactNode;
    moduleName: string;
    locale: 'hr' | 'en';
    onResetToDashboard?: () => void;
};

type State = {
    error: Error | null;
    errorId: string | null;
};

function shortId(): string {
    const t = Date.now().toString(36).slice(-4);
    const r = Math.random().toString(36).slice(2, 8);
    return `req_${t}${r}`;
}

export class BiErrorBoundary extends React.Component<Props, State> {
    state: State = { error: null, errorId: null };

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { error, errorId: shortId() };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        // Surface to the browser console so a developer or the user (with DevTools open)
        // can copy the stack and componentStack alongside the displayed errorId.
        console.error('[BiErrorBoundary]', this.props.moduleName, this.state.errorId, error, info.componentStack);
    }

    private handleRetry = () => {
        this.setState({ error: null, errorId: null });
    };

    render() {
        const { error, errorId } = this.state;
        if (!error) return this.props.children;

        const { moduleName, locale, onResetToDashboard } = this.props;
        const en = locale === 'en';

        const title = en
            ? `The ${moduleName} module is currently unavailable`
            : `Modul ${moduleName} trenutačno nije dostupan`;
        const techLabel = en ? 'Technical error' : 'Tehnička pogreška';
        const tryLater = en
            ? 'Try again or return to the Dashboard.'
            : 'Pokušaj kasnije ili se vrati na Dashboard.';
        const supportNote = en
            ? 'If this keeps happening, send development this request ID:'
            : 'Ako se ponavlja, javi razvoju s ovim ID-em zahtjeva:';
        const retryLabel = en ? '↻ Try again' : '↻ Pokušaj ponovno';
        const dashboardLabel = en ? '← Back to Dashboard' : '← Natrag na Dashboard';

        return (
            <section className="card bi-errstate" role="alert" aria-live="polite">
                <div className="bi-errstate-row">
                    <div className="bi-errstate-ico" aria-hidden="true">⚠</div>
                    <div className="bi-errstate-body">
                        <strong className="bi-errstate-title">{title}</strong>
                        <p className="bi-errstate-tech">
                            {techLabel}: <code>{error.message || error.name}</code>.
                        </p>
                        <p className="bi-errstate-hint">{tryLater}</p>
                        {errorId ? (
                            <p className="bi-errstate-rid">
                                {supportNote} <code>{errorId}</code>
                            </p>
                        ) : null}
                        <div className="bi-errstate-actions">
                            <button type="button" className="compact" onClick={this.handleRetry}>
                                {retryLabel}
                            </button>
                            {onResetToDashboard ? (
                                <button type="button" className="compact" onClick={onResetToDashboard}>
                                    {dashboardLabel}
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </section>
        );
    }
}
